import React, { useState, useEffect } from "react";
import { DashboardLayout } from "src/components/dashboard";
import { useNavigate, useParams } from "react-router-dom";
import CentralPartnerBasicInfoForm from "src/components/partners/CentralManager";
import PartnerFormHeader from "src/components/partners/PartnerFormHeader";
import PartnerFormNavigation from "src/components/partners/PartnerFormNavigation";
import { useGetAdminQuery } from "src/store/services/adminApi";
import {
  AddCentralPartnerPayload,
  useAddCentralPartnerMutation,
} from "src/store/services/partnerApi";
import { toast, ToastContainer } from "react-toastify";

// ======================
// FORM TYPE (FIXED)
// ======================
type PartnerFormData = {
  outlets: string[];
  adminId: string;
  email: string;
};

const AddNewCentralPartners = () => {
  const navigate = useNavigate();

  // ======================
  // PARAMS (FIXED)
  // ======================
  const { categoryName, adminId } = useParams<{
    categoryName: string;
    adminId: string;
  }>();

  const [addPartner, { isLoading }] = useAddCentralPartnerMutation();

  // ======================
  // ADMIN FETCH (FIXED)
  // ======================
  const { data: adminData, isLoading: adminLoading } =
    useGetAdminQuery(categoryName!, {
      skip: !categoryName,
    });

  const [error, setError] = useState<string | null>(null);

  // ======================
  // FORM STATE (FIXED)
  // ======================
  const [formData, setFormData] = useState<PartnerFormData>({
    outlets: [],
    adminId: "",
    email: "",
  });

  // ======================
  // AUTO-FILL ADMIN DATA
  // ======================
  useEffect(() => {
    if (adminData?.payload?.data?.admin) {
      const admin = adminData.payload.data.admin;

      setFormData((prev) => ({
        ...prev,
        email: admin.email || "",
        adminId: admin._id || "",
      }));
    }
  }, [adminData]);

  // ======================
  // HANDLE INPUT FROM CHILD
  // ======================
  const handleInputChange = (e: any) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ======================
  // TRANSFORM PAYLOAD (FIXED)
  // ======================
  const transformToApiPayload = (): AddCentralPartnerPayload => {
    return {
      email: formData.email.trim(),
      adminId: formData.adminId.trim(),
      outlets: formData.outlets, // ✅ already array
    };
  };

  // ======================
  // SUBMIT
  // ======================
  const handleSubmit = async () => {
    setError(null);

    if (!formData.email) {
      setError("Email not found from admin");
      return;
    }

    if (!formData.outlets.length) {
      setError("Please select an outlet");
      return;
    }

    try {
      const payload = transformToApiPayload();

      const response = await addPartner(payload).unwrap();

      const partnerId =
        response?.payload?.id ||
        response?.payload?._id ||
        response?.payload?.partner_id ||
        response?.payload?.partner?.id ||
        response?.payload?.partner?._id;

      if (!partnerId) {
        console.warn("Partner created but ID missing:", response);
      }

      toast.success("Partner added successfully");

      navigate(`/partners/group/${categoryName || ""}`);
    } catch (err: any) {
      const message =
        err?.data?.message ||
        err?.message ||
        err?.data?.error?.message ||
        "Failed to add partner";

      console.error("ERROR:", message);
      setError(message);
      toast.error(message);
    }
  };

  // ======================
  // NEXT BUTTON
  // ======================
  const handleNext = () => {
    handleSubmit();
  };

  // ======================
  // BACK BUTTON
  // ======================
  const handleBack = () => {
    navigate(`/partners/group/${categoryName || ""}`);
  };

  return (
    <DashboardLayout>
      <div className="p-6">
        <PartnerFormHeader
          title={"Add Central Partner"}
          description={"Select category and outlet for this admin"}
        />

        {/* CHILD COMPONENT */}
        <CentralPartnerBasicInfoForm
          formData={formData}
          onInputChange={handleInputChange}
        />

        {/* NAVIGATION */}
        <PartnerFormNavigation
          onBack={handleBack}
          onNext={handleNext}
          isLoading={isLoading || adminLoading}
          disabled={isLoading || adminLoading}
        />

        {/* ERROR */}
        {error && (
          <p className="text-red-500 mt-4 text-sm">{error}</p>
        )}
      </div>

      <ToastContainer />
    </DashboardLayout>
  );
};

export default AddNewCentralPartners;